import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  onSnapshot,
  writeBatch,
  addDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { STORES, Store } from '../config/stores';
import { chatService } from './chat';
import { UserRole } from '../lib/types/auth';
import { pdfReportService } from './pdfReportService';
import { simplePdfReportService } from './simplePdfReportService';
import { chatFileUploadService } from './chatFileUploadService';

export interface StoreGoal {
  id: string;
  storeId: string;
  storeName: string;
  monthYear: string; // Format: "2025-09"
  goalAmount: number;
  currentSales: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  updatedBy: string;
}

export interface MonthlyGoalReport {
  id: string;
  monthYear: string;
  reportDate: Timestamp;
  goals: {
    storeId: string;
    storeName: string;
    goalAmount: number;
    actualSales: number;
    completionPercentage: number;
  }[];
  totalGoalAmount: number;
  totalActualSales: number;
  overallCompletionPercentage: number;
}

export class StoreSalesGoalsService {
  private static instance: StoreSalesGoalsService;

  static getInstance(): StoreSalesGoalsService {
    if (!StoreSalesGoalsService.instance) {
      StoreSalesGoalsService.instance = new StoreSalesGoalsService();
    }
    return StoreSalesGoalsService.instance;
  }

  private getCurrentMonthYear(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  private getPreviousMonthYear(): string {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
  }

  private formatMonthYear(monthYear: string): string {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  async getGoalsForMonth(monthYear?: string): Promise<StoreGoal[]> {
    try {
      const targetMonth = monthYear || this.getCurrentMonthYear();
      console.log(`StoreSalesGoalsService.getGoalsForMonth called for: ${targetMonth}`);
      
      const goalsRef = collection(db, 'storeGoals');
      const q = query(goalsRef, where('monthYear', '==', targetMonth));
      
      const querySnapshot = await getDocs(q);
      const goals: StoreGoal[] = [];
      
      querySnapshot.forEach((doc) => {
        goals.push({ id: doc.id, ...doc.data() } as StoreGoal);
      });
      
      console.log(`Found ${goals.length} goals for ${targetMonth}:`, goals);
      return goals;
    } catch (error) {
      console.error('Error fetching goals:', error);
      return [];
    }
  }

  subscribeToCurrentMonthGoals(callback: (goals: StoreGoal[]) => void): () => void {
    const currentMonthYear = this.getCurrentMonthYear();
    console.log(`StoreSalesGoalsService.subscribeToCurrentMonthGoals for: ${currentMonthYear}`);
    
    const goalsRef = collection(db, 'storeGoals');
    const q = query(goalsRef, where('monthYear', '==', currentMonthYear));
    
    return onSnapshot(q, (querySnapshot) => {
      const goals: StoreGoal[] = [];
      querySnapshot.forEach((doc) => {
        goals.push({ id: doc.id, ...doc.data() } as StoreGoal);
      });
      
      console.log(`Real-time update: ${goals.length} goals for ${currentMonthYear}:`, goals);
      callback(goals);
    }, (error) => {
      console.error('Error in goals subscription:', error);
      callback([]);
    });
  }

  async updateStoreGoal(storeId: string, goalAmount: number, updatedBy: string): Promise<boolean> {
    try {
      console.log(`StoreSalesGoalsService.updateStoreGoal called:`, { storeId, goalAmount, updatedBy });
      
      const currentMonthYear = this.getCurrentMonthYear();
      const store = STORES.find(s => s.id === storeId);
      
      if (!store) {
        console.error(`Store not found: ${storeId}`);
        return false;
      }

      const goalId = `${storeId}-${currentMonthYear}`;
      const goalRef = doc(db, 'storeGoals', goalId);
      
      const goalData: Omit<StoreGoal, 'id'> = {
        storeId,
        storeName: store.name,
        monthYear: currentMonthYear,
        goalAmount,
        currentSales: 0, // Will be updated separately when sales data changes
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        updatedBy
      };

      await setDoc(goalRef, goalData, { merge: true });
      console.log(`Successfully updated goal for ${store.name}: $${goalAmount}`);
      return true;
    } catch (error) {
      console.error(`Error updating store goal for ${storeId}:`, error);
      return false;
    }
  }

  async updateCurrentSales(storeId: string, currentSales: number): Promise<boolean> {
    try {
      const currentMonthYear = this.getCurrentMonthYear();
      const goalId = `${storeId}-${currentMonthYear}`;
      const goalRef = doc(db, 'storeGoals', goalId);
      
      await updateDoc(goalRef, {
        currentSales,
        updatedAt: Timestamp.now()
      });
      
      return true;
    } catch (error) {
      console.error(`Error updating current sales for ${storeId}:`, error);
      return false;
    }
  }

  async generateMonthlyReport(monthYear?: string, isTest: boolean = false): Promise<boolean> {
    try {
      const targetMonth = monthYear || (isTest ? this.getCurrentMonthYear() : this.getPreviousMonthYear());
      console.log(`🎯 StoreSalesGoalsService.generateMonthlyReport called:`);
      console.log(`   - Target month: ${targetMonth}`);
      console.log(`   - Test mode: ${isTest}`);
      
      const goals = await this.getGoalsForMonth(targetMonth);
      console.log(`   - Found ${goals.length} goals:`, goals);
      
      if (goals.length === 0) {
        console.log('❌ No goals found for the month, skipping report generation');
        return false;
      }

      // Check if any goals have amounts > 0
      const goalsWithAmounts = goals.filter(g => g.goalAmount > 0);
      console.log(`   - Goals with amounts > 0: ${goalsWithAmounts.length}`);
      
      if (goalsWithAmounts.length === 0) {
        console.log('❌ No goals with amounts > 0, skipping report generation');
        return false;
      }

      // Calculate report data
      const reportGoals = goals.map(goal => ({
        storeId: goal.storeId,
        storeName: goal.storeName,
        goalAmount: goal.goalAmount,
        actualSales: goal.currentSales,
        completionPercentage: goal.goalAmount > 0 ? (goal.currentSales / goal.goalAmount) * 100 : 0
      }));

      const totalGoalAmount = reportGoals.reduce((sum, g) => sum + g.goalAmount, 0);
      const totalActualSales = reportGoals.reduce((sum, g) => sum + g.actualSales, 0);
      const overallCompletionPercentage = totalGoalAmount > 0 ? (totalActualSales / totalGoalAmount) * 100 : 0;

      const report: Omit<MonthlyGoalReport, 'id'> = {
        monthYear: targetMonth,
        reportDate: Timestamp.now(),
        goals: reportGoals,
        totalGoalAmount,
        totalActualSales,
        overallCompletionPercentage
      };

      // Save report to Firestore
      console.log(`💾 Saving report to Firestore...`);
      const reportRef = doc(collection(db, 'monthlyGoalReports'));
      await setDoc(reportRef, report);
      console.log(`✅ Report saved to Firestore with ID: ${reportRef.id}`);

      // Send report to Master Admin
      console.log(`📨 Sending report to Master Admin...`);
      await this.sendReportToMasterAdmin(report, targetMonth);

      // Reset goals for new month (only if not test mode)
      if (!isTest) {
        console.log('Resetting goals for new month...');
        await this.resetGoalsForNewMonth();
      } else {
        console.log('Test mode: Skipping goal reset');
      }

      return true;
    } catch (error) {
      console.error('Error generating monthly report:', error);
      return false;
    }
  }

  private async sendReportToMasterAdmin(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): Promise<void> {
    try {
      console.log('📨 Sending monthly goal report to Master Admin...');
      
      // Find or create Master Admin chat
      console.log('🔍 Finding or creating Master Admin chat...');
      const chatId = await this.findOrCreateMasterAdminChat();
      if (!chatId) {
        console.error('❌ Could not find or create Master Admin chat');
        return;
      }
      console.log(`✅ Chat ID: ${chatId}`);

      // Always send the FIN Chat message first (most important)
      const message = this.formatReportMessage(report, monthYear);
      console.log('📤 Sending FIN Chat message...');
      const success = await this.sendMessageToChat(chatId, message);
      
      if (success) {
        console.log('✅ Monthly goal report sent successfully to FIN Chat');
      } else {
        console.error('❌ Failed to send monthly goal report to FIN Chat');
        return; // Don't continue if FIN Chat failed
      }

      // Try to generate and upload PDF to FIN Chat
      try {
        console.log('📄 Attempting complex PDF generation...');
        const pdfBlob = await pdfReportService.generateStoreGoalReportPDF(report, monthYear);
        console.log('✅ Complex PDF generated successfully');
        
        // Upload PDF to Firebase Storage and attach to FIN Chat
        const monthName = this.formatMonthYear(monthYear);
        const fileName = `Store_Goals_Report_${monthName.replace(' ', '_')}.pdf`;
        
        console.log('📤 Uploading PDF to Firebase Storage...');
        const uploadResult = await chatFileUploadService.uploadPDFToChat(pdfBlob, fileName, chatId);
        console.log('✅ PDF uploaded to Firebase Storage successfully');
        
        // Send message with PDF attachment
        const pdfMessage = `📊 **Detailed PDF Report - ${monthName}**\nComplete store goals analysis with charts and insights.`;
        const attachmentSuccess = await chatService.sendMessageWithAttachment(chatId, 'system', pdfMessage, uploadResult);
        
        if (attachmentSuccess) {
          console.log('✅ PDF attachment sent to FIN Chat successfully');
        } else {
          console.error('❌ Failed to send PDF attachment to FIN Chat');
        }
        
      } catch (pdfError) {
        console.error('⚠️ Complex PDF generation failed, trying simple PDF...', pdfError);
        
        // Try simple PDF as fallback
        try {
          console.log('📄 Attempting simple PDF generation...');
          const simplePdfBlob = await simplePdfReportService.generateSimpleStoreGoalReportPDF(report, monthYear);
          console.log('✅ Simple PDF generated successfully');
          
          // Upload simple PDF to Firebase Storage and attach to FIN Chat
          const monthName = this.formatMonthYear(monthYear);
          const fileName = `Store_Goals_Report_${monthName.replace(' ', '_')}_Simple.pdf`;
          
          console.log('📤 Uploading simple PDF to Firebase Storage...');
          const uploadResult = await chatFileUploadService.uploadPDFToChat(simplePdfBlob, fileName, chatId);
          console.log('✅ Simple PDF uploaded to Firebase Storage successfully');
          
          // Send message with simple PDF attachment
          const simplePdfMessage = `📄 **Simple PDF Report - ${monthName}**\nBasic store goals report with essential data.`;
          const attachmentSuccess = await chatService.sendMessageWithAttachment(chatId, 'system', simplePdfMessage, uploadResult);
          
          if (attachmentSuccess) {
            console.log('✅ Simple PDF attachment sent to FIN Chat successfully');
          } else {
            console.error('❌ Failed to send simple PDF attachment to FIN Chat');
          }
          
        } catch (simplePdfError) {
          console.error('❌ Both PDF generation methods failed:', simplePdfError);
          
          // Send error message to FIN Chat
          const errorMessage = `⚠️ **PDF Generation Issue**\nThe monthly report was generated, but there was an issue creating the PDF. The data above is still accurate.`;
          try {
            await this.sendMessageToChat(chatId, errorMessage);
          } catch (errorMsgError) {
            console.error('Failed to send PDF error message:', errorMsgError);
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Error sending report to Master Admin:', error);
    }
  }

  private async findOrCreateMasterAdminChat(): Promise<string | null> {
    try {
      console.log('Looking for existing Store Goal Reports chat...');
      
      // First, try to find existing "Store Goal Reports" chat
      const chatsRef = collection(db, 'chats');
      const q = query(chatsRef, where('name', '==', 'Store Goal Reports'));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const chatId = querySnapshot.docs[0].id;
        console.log('Found existing Store Goal Reports chat:', chatId);
        return chatId;
      }

      console.log('Creating new Store Goal Reports chat...');
      
      // Get all Master Admin users
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, where('roles', 'array-contains', UserRole.MasterAdmin));
      const usersSnapshot = await getDocs(usersQuery);
      
      const masterAdminEmails: string[] = [];
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          masterAdminEmails.push(userData.email);
        }
      });

      if (masterAdminEmails.length === 0) {
        console.error('No Master Admin users found');
        return null;
      }

      console.log('Found Master Admin emails:', masterAdminEmails);

      // Create new chat with Master Admins
      const chatData = {
        name: 'Store Goal Reports',
        participants: masterAdminEmails,
        lastMessage: null,
        updatedAt: Timestamp.now(),
        isPrivate: false,
        createdBy: 'system',
        createdAt: Timestamp.now(),
        isSystemChat: true
      };

      const chatRef = doc(collection(db, 'chats'));
      await setDoc(chatRef, chatData);
      
      console.log('Created new Store Goal Reports chat:', chatRef.id);
      return chatRef.id;
    } catch (error) {
      console.error('Error finding/creating Master Admin chat:', error);
      return null;
    }
  }

  private async sendMessageToChat(chatId: string, messageText: string): Promise<boolean> {
    try {
      console.log('Sending message to chat:', chatId);
      
      const message = {
        sender: 'system',
        text: messageText,
        timestamp: Timestamp.now()
      };

      // Add the message to the messages subcollection
      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      // Update the parent chat document
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        lastMessage: messageText.substring(0, 100) + (messageText.length > 100 ? '...' : ''),
        updatedAt: Timestamp.now()
      });

      console.log('Message sent successfully to FIN Chat');
      return true;
    } catch (error) {
      console.error('Error sending message to chat:', error);
      return false;
    }
  }

  private formatReportMessage(report: Omit<MonthlyGoalReport, 'id'>, monthYear: string): string {
    const monthName = this.formatMonthYear(monthYear);
    
    let message = `📊 **Store Goal Report - ${monthName}**\n\n`;
    message += `**Overall Performance:**\n`;
    message += `• Total Goal: $${report.totalGoalAmount.toLocaleString()}\n`;
    message += `• Total Sales: $${report.totalActualSales.toLocaleString()}\n`;
    message += `• Completion: ${report.overallCompletionPercentage.toFixed(1)}%\n\n`;
    
    message += `**Store Performance:**\n`;
    
    // Sort by completion percentage (highest first)
    const sortedGoals = [...report.goals].sort((a, b) => b.completionPercentage - a.completionPercentage);
    
    sortedGoals.forEach(goal => {
      const emoji = goal.completionPercentage >= 100 ? '🎯' : 
                   goal.completionPercentage >= 80 ? '📈' : 
                   goal.completionPercentage >= 60 ? '📊' : '📉';
      
      message += `${emoji} **${goal.storeName}**: $${goal.actualSales.toLocaleString()} / $${goal.goalAmount.toLocaleString()} (${goal.completionPercentage.toFixed(1)}%)\n`;
    });
    
    message += `\n*Goals have been reset for the new month.*`;
    
    return message;
  }

  private async resetGoalsForNewMonth(): Promise<void> {
    try {
      console.log('Resetting all goals to 0 for new month...');
      
      const currentMonthYear = this.getCurrentMonthYear();
      const goals = await this.getGoalsForMonth(currentMonthYear);
      
      const batch = writeBatch(db);
      
      goals.forEach(goal => {
        const goalRef = doc(db, 'storeGoals', goal.id);
        batch.update(goalRef, {
          goalAmount: 0,
          currentSales: 0,
          updatedAt: Timestamp.now(),
          updatedBy: 'system'
        });
      });
      
      await batch.commit();
      console.log('Successfully reset all goals for new month');
    } catch (error) {
      console.error('Error resetting goals for new month:', error);
    }
  }
}

export const storeSalesGoalsService = StoreSalesGoalsService.getInstance();